import { createTheme } from '@mui/material/styles';

/**
 * Karofi Theme — synchronized with Flutter CRM native app.
 *
 * Color source: lib/resources/colors.dart (MyColors)
 * Typography source: lib/resources/styles.dart (TextStyles) + theme_builder.dart
 * Spacing source: lib/resources/dimens.dart (Dimens)
 */
export const karofiTheme = createTheme({
  palette: {
    primary: {
      main: '#1B8D4F',       // webEco1Green
      light: '#42CC48',      // webEco2Green
      dark: '#303729',       // greenDark
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#CB3329',       // webEco7Red
      light: '#EB5757',      // redColor
      dark: '#DD240E',       // borderError
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#F28627',       // webEco3Orange
      light: '#FFBF65',      // colorMoveCalendar
      dark: '#E65100',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#01A5E4',       // colorInProgress
      light: '#12BBE0',      // webEco4Blue
      dark: '#0563C1',       // colorBluePhone
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#1B8D4F',       // webEco1Green
      light: '#42CC48',      // webEco2Green
      dark: '#303729',       // greenDark
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F5F5F5',    // webEco0White (backgroundWhiteF5)
      paper: '#FFFFFF',      // primaryWhite
    },
    text: {
      primary: '#3D2A2A',    // primaryDark (textDark)
      secondary: '#909090',  // webEco9Gray (textGray_3)
      disabled: '#BBBBBB',   // webEco8Gray (textGray_2)
    },
    divider: '#E5E5E5',      // webEco12Gray (borderGrayLight)
    action: {
      active: '#1B8D4F',
      hover: 'rgba(27, 141, 79, 0.08)',
      selected: 'rgba(27, 141, 79, 0.12)',
      disabled: '#C4C4C4',   // webEco11Gray
      disabledBackground: '#E5E5E5',
    },
  },
  typography: {
    fontFamily: [
      '"Be Vietnam Pro"',
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    // Flutter: displayLarge = textSize26.bold
    h1: {
      fontSize: '1.625rem',   // 26px
      fontWeight: 700,
      lineHeight: 1.2,
      color: '#3D2A2A',
    },
    // Flutter: displayMedium = textSize24.bold
    h2: {
      fontSize: '1.5rem',     // 24px
      fontWeight: 700,
      lineHeight: 1.3,
      color: '#3D2A2A',
    },
    // Flutter: displaySmall = textSize20.bold
    h3: {
      fontSize: '1.25rem',    // 20px
      fontWeight: 700,
      lineHeight: 1.4,
      color: '#3D2A2A',
    },
    // Flutter: headlineLarge = textSize18.bold
    h4: {
      fontSize: '1.125rem',   // 18px
      fontWeight: 700,
      lineHeight: 1.4,
      color: '#3D2A2A',
    },
    // Flutter: headlineMedium = textSize14.semiBold
    h5: {
      fontSize: '0.875rem',   // 14px
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#3D2A2A',
    },
    // Flutter: headlineSmall = textSize13.medium
    h6: {
      fontSize: '0.8125rem',  // 13px
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#3D2A2A',
    },
    // Flutter: bodyLarge = textSize14.medium, h1.5
    body1: {
      fontSize: '0.875rem',   // 14px
      fontWeight: 500,
      lineHeight: 1.5,
    },
    // Flutter: bodyMedium = textSize13.medium
    body2: {
      fontSize: '0.8125rem',  // 13px
      fontWeight: 400,
      lineHeight: 1.43,
    },
    // Flutter: bodySmall = textSize13.regular
    caption: {
      fontSize: '0.75rem',    // 12px
      fontWeight: 400,
      lineHeight: 1.66,
    },
    // Flutter: labelLarge = textSize16.bold
    button: {
      fontSize: '0.875rem',   // 14px
      fontWeight: 600,
      textTransform: 'none',
    },
    subtitle1: {
      fontSize: '0.875rem',   // 14px
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.8125rem',  // 13px
      fontWeight: 500,
      lineHeight: 1.43,
    },
    overline: {
      fontSize: '0.625rem',   // 10px
      fontWeight: 400,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
  },
  shape: {
    borderRadius: 12,          // Dimens.size12 — Flutter default
  },
  spacing: 4,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F5F5F5',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,      // Dimens.size12
          padding: '12px 16px',  // Flutter ElevatedButton padding
          fontSize: '0.875rem',
          fontWeight: 600,
          minHeight: 48,         // Dimens.size48
          minWidth: 48,
          letterSpacing: '0.02em',
          '&:focus-visible': {
            outline: '2px solid #1B8D4F',
            outlineOffset: 2,
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
            transform: 'scale(0.98)',
          },
        },
        outlined: {
          borderColor: '#1B8D4F',
          borderWidth: 1,
          '&:hover': {
            borderWidth: 1,
            backgroundColor: 'rgba(27, 141, 79, 0.05)',
          },
        },
        sizeSmall: {
          minHeight: 36,
          padding: '6px 12px',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
          '&:focus-visible': {
            outline: '2px solid #1B8D4F',
            outlineOffset: 2,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 10,
          color: '#909090',
          '&.Mui-checked': {
            color: '#1B8D4F',
          },
          '&:focus-visible': {
            outline: '2px solid #1B8D4F',
            outlineOffset: 2,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 10,
          color: '#909090',
          '&.Mui-checked': {
            color: '#1B8D4F',
          },
          '&:focus-visible': {
            outline: '2px solid #1B8D4F',
            outlineOffset: 2,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#FFFFFF',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E5E5E5',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#BBBBBB',
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1B8D4F',
                borderWidth: 2,
              },
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem',
            '&::placeholder': {
              color: '#BCBCBC',
              opacity: 1,
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            color: '#909090',
            '&.Mui-focused': {
              color: '#1B8D4F',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.875rem',
          '&:focus-visible': {
            outline: '2px solid #1B8D4F',
            outlineOffset: 2,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
          border: '1px solid #F1F1F1',
          overflow: 'hidden',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
        },
        colorSuccess: {
          backgroundColor: 'rgba(27, 141, 79, 0.12)',
          color: '#1B8D4F',
        },
        colorError: {
          backgroundColor: 'rgba(203, 51, 41, 0.12)',
          color: '#CB3329',
        },
        colorWarning: {
          backgroundColor: 'rgba(242, 134, 39, 0.12)',
          color: '#F28627',
        },
        colorInfo: {
          backgroundColor: 'rgba(1, 165, 228, 0.12)',
          color: '#01A5E4',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#1B8D4F',
          '&:focus-visible': {
            outline: '2px solid #1B8D4F',
            outlineOffset: 2,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E5E5E5',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontSize: '0.8125rem',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            borderRadius: 12,
          },
        },
      },
    },
  },
});
